import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const sampleVendors = [
  {
    name: 'Amazon',
    rating: 4.6,
    reviewCount: 1247,
    price: 1699,
    originalPrice: 2299,
    deliveryTime: '1–2 days Prime',
    productName: 'Apple MacBook Pro 14" M3 Pro — 18GB RAM, 512GB SSD',
    overallScore: 95,
    isWinner: true,
    criteria: [
      { name: 'Processor', icon: 'Cpu', score: 98, status: 'excellent' },
      { name: 'Memory', icon: 'MemoryStick', score: 100, status: 'excellent' },
      { name: 'Price', icon: 'DollarSign', score: 95, status: 'excellent' },
      { name: 'Delivery', icon: 'Truck', score: 100, status: 'excellent' },
    ],
    terms: 'Free returns within 30 days. 1-year Apple warranty included.',
  },
  {
    name: 'Best Buy',
    rating: 4.4,
    reviewCount: 892,
    price: 1399,
    originalPrice: null,
    deliveryTime: 'Same day pickup',
    productName: 'Apple MacBook Pro 14" M3 — 16GB RAM, 512GB SSD',
    overallScore: 88,
    isWinner: false,
    criteria: [
      { name: 'Processor', icon: 'Cpu', score: 85, status: 'good' },
      { name: 'Memory', icon: 'MemoryStick', score: 100, status: 'excellent' },
      { name: 'Price', icon: 'DollarSign', score: 100, status: 'excellent' },
      { name: 'Delivery', icon: 'Truck', score: 95, status: 'excellent' },
    ],
    terms: 'Price match guarantee. Geek Squad protection available.',
  },
  {
    name: 'B&H Photo',
    rating: 4.8,
    reviewCount: 2156,
    price: 2299,
    originalPrice: null,
    deliveryTime: '2–3 days shipping',
    productName: 'Apple MacBook Pro 14" M4 — 16GB RAM, 512GB SSD (Latest)',
    overallScore: 82,
    isWinner: false,
    criteria: [
      { name: 'Processor', icon: 'Cpu', score: 100, status: 'excellent' },
      { name: 'Memory', icon: 'MemoryStick', score: 100, status: 'excellent' },
      { name: 'Price', icon: 'DollarSign', score: 75, status: 'fair' },
      { name: 'Delivery', icon: 'Truck', score: 85, status: 'good' },
    ],
    terms: 'Pro support included. Extended warranty options available.',
  },
];

const criteriaGradients = [
  'linear-gradient(135deg, #F58A54, #F06F6B)',
  'linear-gradient(135deg, #F06F6B, #E0528A)',
  'linear-gradient(135deg, #E0528A, #c026d3)',
  'linear-gradient(135deg, #06b6d4, #3b82f6)',
];

const getScoreColor = (score) => {
  if (score >= 90) return '#22c55e';
  if (score >= 75) return '#3b82f6';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const getPillStyle = (status) => {
  switch (status) {
    case 'excellent': return { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' };
    case 'good': return { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' };
    case 'fair': return { bg: '#fffbeb', text: '#b45309', border: '#fde68a' };
    default: return { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
  }
};

const SampleComparisonPreview = () => {
  const navigate = useNavigate();
  const [selectedVendor, setSelectedVendor] = useState(null);

  return (
    <section className="py-16" style={{ background: 'linear-gradient(180deg, #fff5f0 0%, #fff0f5 50%, #f5f0ff 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)', boxShadow: '0 4px 14px rgba(245,138,84,0.3)' }}>
            <Icon name="Laptop" size={15} color="white" />
            <span className="text-white text-sm font-semibold">Live Example</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            See Stackd in Action
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Here's what a real laptop comparison looks like — three vendors, scored side-by-side on what matters most to you.
          </p>
        </div>

        {/* Comparison header bar */}
        <div
          className="rounded-2xl overflow-hidden mb-5"
          style={{ background: 'white', border: '1.5px solid #fde8d8', boxShadow: '0 2px 12px rgba(245,138,84,0.08)' }}
        >
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="LayoutGrid" size={20} color="white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Side-by-Side Comparison</h3>
                <p className="text-xs text-gray-400">MacBook Pro 14" — 3 vendors scored on your criteria</p>
              </div>
            </div>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="Trophy" size={15} color="white" />
              <span className="text-sm font-semibold text-white">Best: Amazon</span>
            </div>
          </div>
        </div>

        {/* Vendor cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          {sampleVendors?.map((vendor, index) => {
            const isSelected = selectedVendor === vendor?.name;
            const scoreColor = getScoreColor(vendor?.overallScore);

            return (
              <div
                key={vendor?.name}
                className="relative rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{
                  border: isSelected
                    ? '2.5px solid #F58A54'
                    : vendor?.isWinner
                    ? '2.5px solid #F06F6B' :'1.5px solid #e5e7eb',
                  boxShadow: isSelected
                    ? '0 8px 32px rgba(245,138,84,0.18)'
                    : vendor?.isWinner
                    ? '0 8px 24px rgba(240,111,107,0.14)'
                    : '0 2px 8px rgba(0,0,0,0.06)',
                  background: 'white',
                }}
                onClick={() => setSelectedVendor(isSelected ? null : vendor?.name)}
              >
                {/* Winner ribbon */}
                {vendor?.isWinner && (
                  <div
                    className="absolute top-0 left-0 right-0 py-1.5 flex items-center justify-center gap-1.5 z-10"
                    style={{ background: 'linear-gradient(90deg, #F58A54, #F06F6B, #E0528A)' }}
                  >
                    <Icon name="Crown" size={13} color="white" />
                    <span className="text-xs font-bold text-white tracking-wide">TOP PICK</span>
                  </div>
                )}
                {isSelected && !vendor?.isWinner && (
                  <div
                    className="absolute top-0 left-0 right-0 py-1.5 flex items-center justify-center gap-1.5 z-10"
                    style={{ background: 'linear-gradient(90deg, #F58A54, #E0528A)' }}
                  >
                    <Icon name="CheckCircle2" size={13} color="white" />
                    <span className="text-xs font-bold text-white tracking-wide">YOUR PICK</span>
                  </div>
                )}

                <div className={`${vendor?.isWinner || isSelected ? 'pt-9' : 'pt-5'} px-5 pb-5`}>
                  {/* Vendor header + score ring */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                      >
                        <Icon name="Store" size={24} color="white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-base">{vendor?.name}</h4>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Icon name="Star" size={13} className="text-amber-400" />
                          <span className="text-sm font-semibold text-gray-700">{vendor?.rating}</span>
                          <span className="text-xs text-gray-400">({vendor?.reviewCount?.toLocaleString()})</span>
                        </div>
                      </div>
                    </div>

                    {/* Score ring */}
                    <div className="flex flex-col items-center">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{
                          background: `conic-gradient(${scoreColor} ${vendor?.overallScore * 3.6}deg, #f3f4f6 0deg)`,
                          padding: '3px',
                        }}
                      >
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <span className="text-sm font-bold" style={{ color: scoreColor }}>{vendor?.overallScore}</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 mt-1">Score</span>
                    </div>
                  </div>

                  {/* Product name */}
                  <p className="text-sm font-semibold mb-1 line-clamp-2" style={{ color: '#F06F6B' }}>
                    {vendor?.productName}
                  </p>

                  {/* Price + delivery */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">${vendor?.price?.toLocaleString()}</span>
                      {vendor?.originalPrice && (
                        <span className="text-sm text-gray-400 line-through ml-2">${vendor?.originalPrice?.toLocaleString()}</span>
                      )}
                    </div>
                    <div
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                    >
                      <Icon name="Truck" size={11} />
                      <span>{vendor?.deliveryTime}</span>
                    </div>
                  </div>

                  {/* Criteria score bars */}
                  <div className="space-y-2.5 mb-4">
                    {vendor?.criteria?.map((criterion, idx) => {
                      const pillStyle = getPillStyle(criterion?.status);
                      return (
                        <div key={criterion?.name} className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: criteriaGradients?.[idx % criteriaGradients?.length] }}
                          >
                            <Icon name={criterion?.icon} size={12} color="white" />
                          </div>
                          <span className="text-xs text-gray-600 flex-1 truncate">{criterion?.name}</span>
                          <div className="flex items-center gap-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${Math.max(criterion?.score * 0.5, 4)}px`,
                                background: getScoreColor(criterion?.score),
                                minWidth: '4px',
                                maxWidth: '50px',
                              }}
                            />
                            <span
                              className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ background: pillStyle?.bg, color: pillStyle?.text, border: `1px solid ${pillStyle?.border}` }}
                            >
                              {criterion?.score}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Terms */}
                  <div
                    className="text-xs text-gray-500 px-3 py-2 rounded-lg mb-4"
                    style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
                  >
                    <Icon name="FileText" size={11} className="inline mr-1 text-gray-400" />
                    {vendor?.terms}
                  </div>

                  {/* Choose button */}
                  <button
                    className="w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2"
                    style={{
                      background: isSelected
                        ? 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)'
                        : 'white',
                      color: isSelected ? 'white' : '#F58A54',
                      border: isSelected ? 'none' : '2px solid #F58A54',
                      boxShadow: isSelected ? '0 4px 14px rgba(245,138,84,0.35)' : 'none',
                    }}
                    onClick={(e) => { e?.stopPropagation(); setSelectedVendor(isSelected ? null : vendor?.name); }}
                  >
                    <Icon name={isSelected ? 'CheckCircle2' : 'ShoppingBag'} size={15} />
                    {isSelected ? 'Selected!' : 'Choose This'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA strip */}
        {selectedVendor ? (
          <div
            className="rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-6"
            style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', border: '1.5px solid #fde8d8' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="CheckCircle2" size={20} color="white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Ready to go with {selectedVendor}?</p>
                <p className="text-sm text-gray-500">This is just a preview — create your own Stack to get real quotes!</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/create-project-flow')}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)', boxShadow: '0 4px 16px rgba(245,138,84,0.35)' }}
            >
              <Icon name="Plus" size={17} color="white" />
              Create Your Stack
            </button>
          </div>
        ) : (
          <div className="text-center mt-2 mb-6">
            <button
              onClick={() => navigate('/create-project-flow')}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-white font-bold text-sm transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)', boxShadow: '0 4px 16px rgba(245,138,84,0.3)' }}
            >
              <Icon name="BarChart3" size={17} color="white" />
              Try the Full Comparison Tool
            </button>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <Icon name="Info" size={13} />
          <span>This is a sample preview. Real comparisons are powered by live vendor quotes and your custom scoring weights.</span>
        </div>
      </div>
    </section>
  );
};

export default SampleComparisonPreview;
